import React, { useContext, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { LanguageContext } from '../LanguageContext';
import { Panel } from '../renderer/renderers';

function useQuery() {
  const { search } = useLocation();
  return useMemo(() => new URLSearchParams(search), [search]);
}

export default function SubmissionConfirmation() {
  const { language } = useContext(LanguageContext) || { language: 'en' };
  const q = useQuery();
  const navigate = useNavigate();
  const ref = q.get('ref') || '';
  const ts = q.get('ts') || '';
  // Format timestamp to locale-friendly string if present
  const formattedTs = useMemo(() => {
    if (!ts) return '';
    try {
      const d = new Date(ts);
      if (Number.isNaN(d.getTime())) return ts;
      return d.toLocaleString(language === 'fr' ? 'fr-CA' : 'en-CA', { dateStyle: 'long', timeStyle: 'short' });
    } catch (_) { return ts; }
  }, [ts, language]);

  const title = language === 'fr' ? 'Demande soumise' : 'Application submitted';
  const body = language === 'fr' ? 'Votre demande a bien été reçue.' : 'Your application has been received.';

  const nextStepsTitle = language === 'fr' ? 'Prochaines étapes' : 'Next steps';
  const nextStepsBody = language === 'fr'
    ? 'Vous n’avez pas besoin d’envoyer votre demande de nouveau. Conservez votre identifiant de suivi si cela vous aide. Nous examinerons votre demande et nous communiquerons avec vous par le portail ou par courriel si d’autres renseignements sont nécessaires.'
    : 'You do not need to submit your application again. Keep your tracking ID if it is helpful. We will review your application and contact you through the portal or by email if more information is needed.';

  const printLabel = language === 'fr' ? 'Imprimer la confirmation' : 'Print confirmation';
  const dashboardLabel = language === 'fr' ? 'Retour au tableau de bord' : 'Return to dashboard';

  function handlePrint() {
    const title = language === 'fr' ? 'Confirmation de la soumission' : 'Submission confirmation';
    const org = language === 'fr' ? 'Portail public' : 'Public Portal';
    const tsLabel = language === 'fr' ? 'Date et heure de soumission' : 'Submission timestamp';
    const refLabel = language === 'fr' ? 'ID de suivi' : 'Tracking ID';
    const refVal = ref || (language === 'fr' ? 'Non disponible' : 'Not available');
    const tsVal = formattedTs || (language === 'fr' ? 'Non disponible' : 'Not available');
    const html = `<!doctype html>
<html lang="${language}">
  <head>
    <meta charset="utf-8" />
    <meta name="viewport" content="width=device-width, initial-scale=1" />
    <title>${title}</title>
    <style>
      @page { size: auto; margin: 12mm; }
      html, body { background: #fff; color: #000; font-family: -apple-system, BlinkMacSystemFont, Segoe UI, Roboto, Helvetica, Arial, sans-serif; }
      .wrap { max-width: 800px; margin: 0 auto; }
      .panel { border: 2px solid #0b0c0c; padding: 16px 20px; margin-top: 8px; }
      .panel h1 { margin: 0 0 8px 0; font-size: 24px; }
      .panel p { margin: 0; font-size: 16px; }
      .meta { margin-top: 24px; }
      .row { display: flex; gap: 12px; margin: 6px 0; }
      .key { width: 260px; font-weight: 700; }
      .val { flex: 1; }
      .org { font-size: 12px; color: #444; margin-top: 28px; }
    </style>
  </head>
  <body>
    <div class="wrap">
      <div class="panel">
        <h1>${title}</h1>
        <p>${language === 'fr' ? 'Votre demande a bien été reçue.' : 'Your application has been received.'}</p>
      </div>
      <div class="meta">
        <div class="row"><div class="key">${refLabel}</div><div class="val"><strong>${refVal}</strong></div></div>
        <div class="row"><div class="key">${tsLabel}</div><div class="val">${tsVal}</div></div>
      </div>
      <div class="org">${org}</div>
    </div>
  </body>
</html>`;
    try {
      const blob = new Blob([html], { type: 'text/html' });
      const url = URL.createObjectURL(blob);
      const w = window.open(url, '_blank', 'width=900,height=700');
      if (!w) return;
      const trigger = () => {
        try { w.focus(); w.print(); } catch(_) {}
        setTimeout(() => { try { w.close(); } catch(_) {} URL.revokeObjectURL(url); }, 400);
      };
      // Some browsers fire onload for blob URLs
      if ('onload' in w) {
        w.onload = trigger;
      } else {
        // Fallback: give it a moment then try
        setTimeout(trigger, 600);
      }
    } catch (_) {
      // Fallback to in-place print if anything fails
      window.print();
    }
  }

  return (
    <div className="govuk-width-container" style={{ marginTop: '2rem', marginBottom: '2rem' }}>
      <Panel comp={{ title: { en: title, fr: title }, body: { en: body, fr: body } }} lang={language} />
      <div className="govuk-summary-list govuk-!-margin-top-6">
        <div className="govuk-summary-list__row">
          <dt className="govuk-summary-list__key">{language === 'fr' ? 'ID de suivi' : 'Tracking ID'}</dt>
          <dd className="govuk-summary-list__value"><strong>{ref || (language === 'fr' ? 'Non disponible' : 'Not available')}</strong></dd>
        </div>
        <div className="govuk-summary-list__row">
          <dt className="govuk-summary-list__key">{language === 'fr' ? 'Date et heure de soumission' : 'Submission timestamp'}</dt>
          <dd className="govuk-summary-list__value">{formattedTs || (language === 'fr' ? 'Non disponible' : 'Not available')}</dd>
        </div>
      </div>
      <h2 className="govuk-heading-m govuk-!-margin-top-6">{nextStepsTitle}</h2>
      <p className="govuk-body">{nextStepsBody}</p>
      <div className="govuk-button-group govuk-!-margin-top-6">
        <button type="button" className="govuk-button" onClick={handlePrint}>{printLabel}</button>
        <button type="button" className="govuk-button govuk-button--secondary" onClick={() => navigate('/dashboard')}>{dashboardLabel}</button>
      </div>
    </div>
  );
}
